import React, { useContext, useState } from 'react';
import { NumberFormatValues } from 'react-number-format';
import { formatFilterPrice, IconEnum, TextInput } from '@investoro/core';
import styled from 'styled-components';
import { CalculatorModuleContext } from 'utils/context/CalculatorModuleContext';
import { CalculatorInputType, CalculatorSettings } from 'utils/types/Calculator';

import AutocompleteCalculatorInput from 'components/calculator/AutocompleteCalculatorInput';
import CalculatorTable, { CalculatorSection } from 'components/calculator/CalculatorTable';
import SaveCalculatorInput from 'components/calculator/SaveCalculatorInput';
import { StyledNumeric } from 'components/calculator/StyledInputs';
import CardsGrid, { CardItem } from 'components/common/CardsGrid';
import InputContainer from 'components/common/InputContainer';
import { activeNotification } from 'components/functions/activeNotification';
import V2HeaderSection from 'components/V2HeaderSection';

const Wrapper = styled.div`
    margin-bottom: 20px;
    width: 100%;
    paddingleft: 20px;
`;

const BoxContentWrapper = styled.div`
    margin: 10px 120px;
    padding: 30px 20px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    background-color: var(--color-white);
    border-radius: var(--box-border-radius);

    @media (max-width: 768px) {
        margin: 10px 20px;
        padding: 20px 10px;
    }
`;
const ButtonWrapper = styled.div`
    display: flex;
    justify-content: end;
    margin: 10px 120px;
    padding: 30px;
    background-color: var(--color-white);
    border-radius: var(--box-border-radius);

    @media (max-width: 768px) {
        margin: 10px 20px;
        padding: 20px 10px;
    }

`;

const InputWrapper = styled.div`
    padding: 0 20px;
    width: 100%;
`;

const BottomWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 20px;
    margin: 10px 120px;
    padding: 30px;
    background-color: var(--color-white);
    border-radius: var(--box-border-radius);

    @media (max-width: 768px) {
        flex-direction: column;
        margin: 10px 20px;
        padding: 20px 10px;
    }
`;

const CalculatorSettingsPage = () => {
    const {
        calculatorModuleState,
        handleChange,
        calculateFlip,
        saveCalculation,
        handleSetCalculationValues,
        handleDeleteCalculation,
        clearCalculation,
        updateTotalField,
        resetCalculationField
    } = useContext(CalculatorModuleContext);

    const [isEditedCurrentPrice, setIsEditedCurrentPrice] = useState(false);
    const [isEditedCertainPrice, setIsEditedCertainPrice] = useState(false);
    const [isEditedArea, setIsEditedArea] = useState(false);

    const handleCurrentPriceChange = (values: NumberFormatValues) => {
        handleChange('currentPrice', Number(values.value) || null);
    };

    const handleCurrentPriceBlur = async () => {
        setIsEditedCurrentPrice(true);
        await calculateFlip();
        activeNotification('Zaktualizowano', 'Aktualna cena została zmieniona', 'success');
        setTimeout(() => setIsEditedCurrentPrice(false), 300);
    };

    const handleCertainPriceChange = (values: NumberFormatValues) => {
        handleChange('certainPrice', Number(values.value) || null);
    };

    const handleCertainPriceBlur = async () => {
        setIsEditedCertainPrice(true);
        await calculateFlip();
        activeNotification('Zaktualizowano', 'Cena po remoncie została zmieniona', 'success');
        setTimeout(() => setIsEditedCertainPrice(false), 300);
    };

    const handleAreaChange = (values: NumberFormatValues) => {
        handleChange('area', Number(values.value) || null);
    };

    const handleAreaBlur = async () => {
        setIsEditedArea(true);
        await calculateFlip();
        activeNotification('Zaktualizowano', 'Metraż został zmieniony', 'success');
        setTimeout(() => setIsEditedArea(false), 300);
    };

    const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        handleChange('address', e.target.value);
    };

    const handleAddressBlur = () => {
        activeNotification('Zaktualizowano', 'Adres został zmieniony', 'success');
    };

    const handleTableFieldChange = (fieldName: keyof CalculatorSettings, value: string) => {
        handleChange(fieldName, value);
    };

    const handleTableFieldBlur = async () => {
        await calculateFlip();
        activeNotification('Zaktualizowano', 'Wartość została zmieniona', 'success');
    };

    const handleUpdateTotalField = async (fieldName: keyof CalculatorSettings, value: string) => {
        await updateTotalField(fieldName, value);
        activeNotification('Zaktualizowano', 'Wartość została przeliczona', 'success');
    };

    const handleResetField = async (fieldName: keyof CalculatorSettings) => {
        await resetCalculationField(fieldName);
    };

    const getRenovationField = (): {
        fieldName: keyof CalculatorSettings;
        value: string;
        title: string;
    } => {
        const area = calculatorModuleState.area || 0;

        if (area >= 1 && area < 30) {
            return {
                fieldName: 'renovationFrom1To30Amount',
                value: calculatorModuleState.renovationFrom1To30Amount || '',
                title: 'Remont za m2 (1-30 m²)'
            };
        } else if (area >= 30 && area < 40) {
            return {
                fieldName: 'renovationFrom30To40Amount',
                value: calculatorModuleState.renovationFrom30To40Amount || '',
                title: 'Remont za m2 (30-40 m²)'
            };
        } else if (area >= 40 && area < 50) {
            return {
                fieldName: 'renovationFrom40To50Amount',
                value: calculatorModuleState.renovationFrom40To50Amount || '',
                title: 'Remont za m2 (40-50 m²)'
            };
        } else if (area >= 50 && area < 60) {
            return {
                fieldName: 'renovationFrom50To60Amount',
                value: calculatorModuleState.renovationFrom50To60Amount || '',
                title: 'Remont za m2 (50-60 m²)'
            };
        } else if (area >= 60) {
            return {
                fieldName: 'renovationFrom60PlusAmount',
                value: calculatorModuleState.renovationFrom60PlusAmount || '',
                title: 'Remont za m2 (60+ m²)'
            };
        }

        return {
            fieldName: 'renovationFrom1To30Amount',
            value: calculatorModuleState.renovationFrom1To30Amount || '',
            title: 'Remont za m2 (1-30 m²)'
        };
    };

    const renovationField = getRenovationField();

    const initialTotalValues: Record<string, string> = calculatorModuleState.costs
        ? {
            flipTime: calculatorModuleState.flipTime || '',
            minProfitAmount: calculatorModuleState.minProfitAmount || '',
            minProfitPercent: calculatorModuleState.costs.minProfitPercent?.toString() || '',
            notary: calculatorModuleState.costs.notary?.toString() || '',
            brokerPercent: calculatorModuleState.costs.broker?.toString() || '',
            rentFees: calculatorModuleState.costs.rentFees?.toString() || '',
            maintenanceFees: calculatorModuleState.costs.maintenanceFees?.toString() || '',
            portalFees: calculatorModuleState.costs.portalFees?.toString() || '',
            photographerAmount: calculatorModuleState.costs.photographerAmount?.toString() || '',
            saleBrokerPercent: calculatorModuleState.costs.saleBroker?.toString() || '',
            pcc: calculatorModuleState.costs.pcc?.toString() || '',
            [renovationField.fieldName]: calculatorModuleState.costs.renovation?.toString() || '',
            userOption: calculatorModuleState.costs.userOption?.toString() || ''
        }
        : {};

    const calculationsTop: CardItem[] = [
        {
            label: 'Maksymalna cena zakupu',
            value: calculatorModuleState.maxPurchasePrice
                ? formatFilterPrice(calculatorModuleState.maxPurchasePrice.toString())
                : '0',
            suffix: 'zł',
            showStar: true
        },
        {
            label: 'Aktualna cena',
            content: <StyledNumeric
                value={calculatorModuleState.currentPrice?.toString() || ''}
                isEdited={isEditedCurrentPrice}
                onValueChange={handleCurrentPriceChange}
                onBlur={handleCurrentPriceBlur}
                thousandSeparator=" "
                decimalScale={0}
                allowNegative={false}
                suffix={' zł '}
                placeholder="0 zł"
                fontSize={'18px'}
                isNotInTable
                hasValue={!!calculatorModuleState.currentPrice}
                isAllowed={(values: NumberFormatValues) => {
                    if (values.value === '') return true;
                    if (values.value.length > 1 && values.value.startsWith('0')) return false;

                    return true;
                }}
            />,
            showStar: true
        },
        {
            label: 'Cena pewniak po remoncie',
            content: <StyledNumeric
                value={calculatorModuleState.certainPrice?.toString() || ''}
                isEdited={isEditedCertainPrice}
                onValueChange={handleCertainPriceChange}
                onBlur={handleCertainPriceBlur}
                thousandSeparator=" "
                decimalScale={0}
                allowNegative={false}
                suffix={' zł '}
                placeholder="0 zł"
                fontSize={'18px'}
                isNotInTable
                hasValue={!!calculatorModuleState.certainPrice}
                isAllowed={(values: NumberFormatValues) => {
                    if (values.value === '') return true;
                    if (values.value.length > 1 && values.value.startsWith('0')) return false;

                    return true;
                }}
            />,
            showStar: true
        },
        {
            label: 'Metraż inwestycji',
            content: <StyledNumeric
                value={calculatorModuleState.area?.toString() || ''}
                isEdited={isEditedArea}
                onValueChange={handleAreaChange}
                onBlur={handleAreaBlur}
                thousandSeparator=" "
                decimalScale={2}
                allowNegative={false}
                suffix={' m² '}
                placeholder="0 m²"
                fontSize={'18px'}
                isNotInTable
                hasValue={!!calculatorModuleState.area}
                isAllowed={(values: NumberFormatValues) => {
                    if (values.value === '') return true;
                    const numValue = Number(values.value);

                    return numValue >= 0 && numValue <= 1000;
                }}
            />,
            showStar: true
        }
    ];

    const calculationsBottom = [
        {
            label: 'Zysk',
            value: calculatorModuleState.profit ? formatFilterPrice(calculatorModuleState.profit.toString()) : '0',
            suffix: 'zł ',
            showStar: true
        },
        {
            label: 'Koszty',
            value: calculatorModuleState.costs?.sumOfCosts ? formatFilterPrice(calculatorModuleState.costs.sumOfCosts.toString()) : '0',
            suffix: 'zł ',
            showStar: true
        },
        {
            label: 'Marża',
            value: calculatorModuleState.margin?.toString() || '0',
            suffix: '% ',
            showStar: true
        }
    ];

    const calculatorStructure: CalculatorSection[] = [
        {
            title: 'Podstawowe parametry',
            fields: [
                {
                    title: 'Maksymalny czas trwania flipa',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'flipTime',
                    value: calculatorModuleState?.flipTime || '',
                    min: 1,
                    max: 36,
                    suffix: 'miesięcy',
                    result: calculatorModuleState?.flipTime || '',
                    totalSuffix: 'miesięcy'
                }
            ]
        },
        {
            title: 'Zysk',
            fields: [
                {
                    title: 'Minimalna kwota zysku',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'minProfitAmount',
                    value: calculatorModuleState?.minProfitAmount || '',
                    min: 1,
                    suffix: 'zł ',
                    result: `${formatFilterPrice(calculatorModuleState?.minProfitAmount)} zł` || '',
                    resultIsEditable: !!calculatorModuleState.costs,
                    totalSuffix: 'zł '
                },
                {
                    title: 'Minimalny procent zysku',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'minProfitPercent',
                    value: calculatorModuleState.minProfitPercent,
                    min: 0,
                    max: 100,
                    suffix: '% ',
                    result: calculatorModuleState.costs?.minProfitPercent != null
                        ? calculatorModuleState.costs.minProfitPercent.toString()
                        : '',
                    totalSuffix: 'zł ',
                    resultIsEditable: !!calculatorModuleState.costs
                }
            ]
        },
        {
            title: 'Koszty',
            fields: [
                {
                    title: 'Notariusz',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'notary',
                    value: calculatorModuleState.notary,
                    min: 0,
                    max: 30000,
                    suffix: 'zł ',

                    result: calculatorModuleState.costs?.notary != null
                        ? calculatorModuleState.costs.notary.toString()
                        : '',
                    resultIsEditable: !!calculatorModuleState.costs,
                    totalSuffix: 'zł '
                },
                {
                    title: 'Pośrednik przy zakupie mieszkania',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'brokerPercent',
                    value: calculatorModuleState.brokerPercent,
                    min: 0,
                    max: 100,
                    suffix: '% ',
                    result: calculatorModuleState.costs?.broker != null
                        ? calculatorModuleState.costs.broker.toString()
                        : '',
                    resultIsEditable: !!calculatorModuleState.costs,
                    totalSuffix: 'zł'
                },
                {
                    title: 'Opłaty eksploatacyjne miesięczne za m2',
                    helperText: 'czynsz',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'rentFees',
                    value: calculatorModuleState.rentFees,
                    min: 0,
                    max: 10000,
                    suffix: 'zł ',
                    result: calculatorModuleState.costs?.rentFees != null
                        ? calculatorModuleState.costs.rentFees.toString()
                        : '',
                    resultIsEditable: !!calculatorModuleState.costs,
                    totalSuffix: 'zł '
                },
                {
                    title: 'Media miesięczne',
                    helperText: 'prąd, gaz podatek od nieruchomości',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'maintenanceFees',
                    value: calculatorModuleState.maintenanceFees,
                    min: 0,
                    max: 1000,
                    suffix: 'zł   ',
                    result: calculatorModuleState.costs?.maintenanceFees != null
                        ? calculatorModuleState.costs.maintenanceFees.toString()
                        : '',
                    resultIsEditable: !!calculatorModuleState.costs,
                    totalSuffix: 'zł'
                },
                {
                    title: 'Ogłoszenia na portalach',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'portalFees',
                    value: calculatorModuleState.portalFees,
                    min: 0,
                    max: 2000,
                    suffix: 'zł ',
                    result: calculatorModuleState.costs?.portalFees != null
                        ? calculatorModuleState.costs.portalFees.toString()
                        : '',
                    resultIsEditable: !!calculatorModuleState.costs,
                    totalSuffix: 'zł '
                },
                {
                    title: 'Fotograf',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'photographerAmount',
                    value: calculatorModuleState.photographerAmount,
                    min: 0,
                    max: 1000,
                    suffix: 'zł ',
                    result: calculatorModuleState.costs?.photographerAmount != null
                        ? calculatorModuleState.costs.photographerAmount.toString()
                        : '',
                    resultIsEditable: !!calculatorModuleState.costs,
                    totalSuffix: 'zł '
                },
                {
                    title: 'Pośrednik do sprzedaży',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'saleBrokerPercent',
                    value: calculatorModuleState.saleBrokerPercent,
                    min: 0,
                    max: 100,
                    suffix: '%',
                    result: calculatorModuleState.costs?.saleBroker != null
                        ? calculatorModuleState.costs.saleBroker.toString()
                        : '',
                    resultIsEditable: !!calculatorModuleState.costs,
                    totalSuffix: 'zł '
                },
                {
                    title: 'Podatek PCC',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'pcc',
                    value: calculatorModuleState.pcc,
                    min: 0,
                    max: 100,
                    suffix: '% ',
                    result: calculatorModuleState.costs?.pcc != null
                        ? calculatorModuleState.costs.pcc.toString()
                        : '',
                    resultIsEditable: !!calculatorModuleState.costs,
                    totalSuffix: 'zł '

                },
                {
                    title: renovationField.title,
                    type: CalculatorInputType.NUMBER,
                    fieldName: renovationField.fieldName,
                    value: renovationField.value,
                    min: 0,
                    max: 2500,
                    suffix: 'zł ',
                    result: calculatorModuleState.costs?.renovation != null
                        ? calculatorModuleState.costs.renovation.toString()
                        : '',
                    resultIsEditable: !!calculatorModuleState.costs,
                    totalSuffix: 'zł '
                },
                {
                    title: 'Pozycja własna',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'userOption',
                    value: calculatorModuleState.userOption ? calculatorModuleState.userOption.toString() : '0',
                    min: 0,
                    suffix: 'zł ',
                    result: calculatorModuleState.costs?.userOption != null
                        ? calculatorModuleState.costs.userOption.toString()
                        : '',
                    resultIsEditable: !!calculatorModuleState.costs,
                    totalSuffix: 'zł '
                }
            ]
        }
    ];

    return (
        <Wrapper>
            <V2HeaderSection
                title={'Kalkulator flipa'}
                handleReset={clearCalculation}
            />

            <BoxContentWrapper>
                <CardsGrid items={calculationsTop} isSmallLabel />
            </BoxContentWrapper>
            <ButtonWrapper>
                <CalculatorTable
                    sections={calculatorStructure}
                    calculatorState={calculatorModuleState}
                    onValueChange={handleTableFieldChange}
                    onUpdateField={handleTableFieldBlur}
                    onUpdateTotalField={handleUpdateTotalField}
                    onResetField={handleResetField}
                    hasThirdColumn
                    initialTotalValues={initialTotalValues}
                />

            </ButtonWrapper>
            <ButtonWrapper>
                <CardsGrid items={calculationsBottom} isSmallLabel />
            </ButtonWrapper>
            <BottomWrapper>
                <InputWrapper>
                    <InputContainer title="Wybierz kalkulację"
                        icon={IconEnum.CALCULATOR}>
                        <AutocompleteCalculatorInput
                            calculationId={calculatorModuleState.calculationId}
                            options={calculatorModuleState.savedCalculations}
                            handleDeleteCalculation={handleDeleteCalculation}
                            handleSetCalculationValues={handleSetCalculationValues}
                        />
                    </InputContainer>
                </InputWrapper>
                <InputWrapper>
                    <InputContainer title="Adres inwestycji"
                        icon={IconEnum.MAP_PIN}>
                        <TextInput
                            value={calculatorModuleState.address}
                            placeholder="Wpisz adres inwestycji..."
                            onChange={handleAddressChange}
                            onBlur={handleAddressBlur}
                        />
                    </InputContainer>
                </InputWrapper>
                <InputWrapper>
                    <SaveCalculatorInput
                        currentName={calculatorModuleState.calculationName}
                        hasExistingCalculation={!!calculatorModuleState.calculationId}
                        onSave={(name: string) => saveCalculation(name)}
                    />
                </InputWrapper>
            </BottomWrapper>
        </Wrapper>
    );
};

export default CalculatorSettingsPage;
